import dayjs from 'dayjs';
import React from 'react';

import UpperTab from '@/core/components/UpperTab';
import { useFF } from '@/core/lib/ff/ff.context';
import type UserDomain from '@/core/lib/new-architecture/domain/user.domain';
import Store from '@/core/lib/new-architecture/store';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import { BadgesPanel } from '@/core/pages/profile/PublicProfile/Status/BadgesPanel';
import { StatusPanel } from '@/core/pages/profile/PublicProfile/Status/StatusPanel';
import { FeatureFlag } from '@/core/types/featureFlag.type';

dayjs.locale('fr');

const ns = 'profile.profile-public';

interface StatusAndBadgesProps {
  user: UserDomain;
}

export const StatusAndBadges: React.FC<StatusAndBadgesProps> = ({ user }) => {
  const hasFFBadges = useFF(FeatureFlag.BADGES);

  return (
    <article className="rounded-2 border border-stroke-secondary p-2 lg:rounded-3 lg:p-6">
      {hasFFBadges ? (
        <StatusAndBadgesTabs user={user} />
      ) : (
        <div className="space-y-5">
          <StatusPanel user={user} />
        </div>
      )}
    </article>
  );
};

// Séparé pour que les queries badges ne partent pas quand le feature flag est éteint
const StatusAndBadgesTabs: React.FC<StatusAndBadgesProps> = ({ user }) => {
  const { t } = useTranslationContext([ns]);
  const { data: badges, isLoading } = Store.badges.useBadges();
  const badgesCount = badges?.filter(({ isChecked }) => isChecked).length ?? 0;

  return (
    <>
      <UpperTab
        tabs={[
          {
            name: t('status.tabs.status', { ns }),
            content: (
              <div className="mt-6 space-y-5">
                <StatusPanel user={user} />
              </div>
            ),
          },
          {
            name: t('status.tabs.badges', { ns }),
            badge: !isLoading && !!badgesCount && <CountBadge count={badgesCount} />,
            content: (
              <div className="mt-6 space-y-5">
                <BadgesPanel user={user} />
              </div>
            ),
          },
        ]}
      />
    </>
  );
};

const CountBadge: React.FC<{ count: number }> = ({ count }) => (
  <span className="leading-none inline-flex h-5 min-w-5 items-center justify-center rounded-10 bg-brand-alt-500 px-2 text-12 font-bold text-content-button-primary">{count}</span>
);
